import React, { forwardRef } from "react";
import {
  Checkbox,
  FormControl,
  FormHelperText,
  TextField,
} from "@mui/material";
import { StyledAutocomplete } from "./SearchableDropdown.styles";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Lazy load react-window only when needed
let VariableSizeList = null;
try {
  const reactWindow = require("react-window");
  VariableSizeList = reactWindow.VariableSizeList;
} catch (e) {
  console.warn("react-window not available, virtualization disabled");
}

// Virtualized Listbox Component for better performance with large datasets
const VirtualizedListbox = React.forwardRef(function VirtualizedListbox(props, ref) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 48;

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemCount * itemSize;
  };

  if (!VariableSizeList) {
    // Fallback to regular rendering if react-window is not available
    return (
      <ul ref={ref} {...other}>
        {children}
      </ul>
    );
  }

  return (
    <div ref={ref}>
      <div {...other}>
        <VariableSizeList
          height={getHeight()}
          width="100%"
          itemSize={() => itemSize}
          itemCount={itemCount}
          overscanCount={5}
        >
          {({ index, style }) => (
            <div style={style}>{itemData[index]}</div>
          )}
        </VariableSizeList>
      </div>
    </div>
  );
});

const CustomSearchableDropdown = forwardRef((props, ref) => {
  const {
    label,
    options,
    optionKey,
    color = "primary",
    textFieldColor, // Optional separate color for TextField (defaults to undefined)
    size = "small",
    required,
    value,
    onChange = () => {},
    errorMessage,
    multiple = false,
    selectAllEnabled = false,
    showSelectedValues = true,
    enableVirtualization = false, // Enable for large datasets (10k+ items)
    placeholder,
    className,
    disableCheckbox = false, // New prop to disable checkbox rendering
    getOptionLabel: customGetOptionLabel, // Allow custom getOptionLabel
    variant = "outlined", // Default variant with border
    ...otherProps
  } = props;

  const customLabel = required ? `${label} *` : label;
  const allOption = { [optionKey]: "Select All" };
  const safeOptions = options || [];
  const allOptions = selectAllEnabled
    ? [allOption, ...safeOptions]
    : safeOptions;

  const handleChange = (event, newValue) => {
    // Filter out disabled options from selection
    const filteredValue = multiple
      ? (newValue || []).filter((option) => option && !option.disabled)
      : newValue && !newValue.disabled
      ? newValue
      : null;
    if (multiple) {
      // Handle the "Select All" functionality
      if (
        filteredValue?.some(
          (option) => option && option[optionKey] === allOption[optionKey]
        )
      ) {
        onChange(
          event,
          safeOptions.filter((opt) => opt && !opt.disabled)
        );
      } else {
        onChange(event, filteredValue);
      }
    } else {
      // For single select, just call onChange
      // Do NOT update inputValue here - let it be managed by focus/blur/input handlers
      onChange(event, filteredValue);
    }
  };

  // Custom filter for large datasets - only show subset of results
  const filterOptions = enableVirtualization
    ? (options, state) => {
        const inputValue = state.inputValue.toLowerCase();
        
        // If no search input, show first 1000 items for browsing
        if (!inputValue) {
          return options.slice(0, 1000);
        }

        // Filter options based on input and limit to 500 results for performance
        const filtered = options.filter((option) =>
          option && option[optionKey] && 
          option[optionKey].toLowerCase().includes(inputValue)
        );
        
        return filtered.slice(0, 500);
      }
    : undefined;

  // Use custom getOptionLabel if provided, otherwise use default
  const defaultGetOptionLabel = (option) => (option ? option[optionKey] || "" : "");
  const finalGetOptionLabel = customGetOptionLabel || defaultGetOptionLabel;

  return (
    <FormControl fullWidth error={!!errorMessage} size={size} className={`bg-white ${className || "m-2"}`}>
      <StyledAutocomplete
        ref={ref}
        id={label}
        className={className}
        options={allOptions}
        autoHighlight
        getOptionLabel={finalGetOptionLabel}
        size={size}
        value={value}
        multiple={multiple}
        disableCloseOnSelect={multiple}
        onChange={handleChange}
        isOptionEqualToValue={(option, selectedValue) =>
          option[optionKey] === selectedValue[optionKey]
        }
        disableListWrap={enableVirtualization}
        filterOptions={filterOptions}
        noOptionsText={enableVirtualization ? "No matching options found. Try different keywords." : "No options"}
        {...(enableVirtualization ? { ListboxComponent: VirtualizedListbox } : {})}
        renderOption={disableCheckbox ? undefined : (props, option, { selected }) => (
          <li
            {...props}
            className={`text-sm ${
              option.disabled ? " opacity-50 pointer-events-none" : ""
            }`}
          >
            <Checkbox
              color={color}
              size={size}
              style={{ marginRight: 8 }}
              checked={selected}
              icon={icon}
              checkedIcon={checkedIcon}
              disabled={option.disabled}
            />
            {option[optionKey]}
          </li>
        )}
        {...(showSelectedValues
          ? {}
          : {
              renderValue: (value) => {
                // Handle the case where value might be null, undefined, or empty
                if (!value) return "";
                if (Array.isArray(value) && value.length === 0) return "";
                return "<></>";
              },
            })}
        renderInput={(params) => {
          const hasValue = multiple 
            ? (value && value.length > 0) 
            : (value && value[optionKey]);
          const placeholderText = !hasValue ? (placeholder || "Select...") : undefined;
          return (
            <TextField
              {...params}
              color={textFieldColor}
              size={size}
              label={label ? customLabel : undefined}
              error={!!errorMessage}
              variant={variant}
              InputLabelProps={label ? {
                shrink: hasValue || !!placeholder,
              } : undefined}
              inputProps={{
                ...params.inputProps,
                placeholder: placeholderText,
              }}
            />
          );
        }}
        {...otherProps}
      />
      {errorMessage && (
        <FormHelperText>{errorMessage}</FormHelperText>
      )}
    </FormControl>
  );
});

CustomSearchableDropdown.displayName = "CustomSearchableDropdown";
export default CustomSearchableDropdown;
